import moment from 'moment';
import { createSelector } from 'reselect';
import { formatNumber } from 'modules/productAnalytics/utils';
import { DATE_FORMAT, DATE_FORMAT_WITH_HOUR_MINS, isDateAfter } from 'utils/date';
import { isMetricPercent } from 'utils/metrics';
import { getCampaignRoute } from 'utils/appRoutes';
import { flatten, keyBy, orderBy as lorderBy } from 'lodash';
import { ActiveMenuItemEnum } from './types';
import { createSummaryViewColumns, createDateTimeViewColumns } from './utils';

const selectReportDetails = (state) => state.reportDetails;
export const selectReportById = (state, props) => state.reports[props.id || props.match.params.id];
export const getActiveReport = (state) => state.reports[state.reports.activeReportId] || {};
export const selectHighLevelMetrics = (state) => state.metrics.highLevelMetrics;
export const dataSourceModal = (state) => state.reportDetails.dataSourceModal;
export const selectReportDetailsTableState = (state) => state.reportDetails.tableState;
export const selectReportDetailsChartState = (state) => state.reportDetails.chartState;
const selectReportDetailsChartActiveView = (state) => state.reportDetails.chartState.activeView;
const selectEntityViewData = (state) =>
  state.reportDetails.tableState.summaryViewData
    ? state.reportDetails.tableState.summaryViewData.data
    : [];
const selectDateTimeViewData = (state) => state.reportDetails.tableState.dateTimeViewData;
export const getEntityViewTableConfig = (state) => state.reportDetails.tableState.tableConfig;
export const selectEntityViewTableConfigOrderBy = (state) =>
  state.reportDetails.tableState.tableConfig.orderBy;
const selectEntityViewTableConfigSorting = (state) =>
  state.reportDetails.tableState.tableConfig.sorting;
export const getReportDataAsCsv = (state) => state.reports.reportDataAsCsv;
export const getReportHasUnsavedChanges = (state) => state.reports.hasUnsavedChanges;

// Collects all unique timestamps in the data and returns them in a sorted array
const dateIntervals = (metrics) =>
  [...new Set(flatten(metrics.map((metric) => metric.data.map((d) => d.time))).sort())].sort();

// Compares intervals to date range and decides to displat date or datetime
const selectReportDateFormat = createSelector(
  [getActiveReport, selectDateTimeViewData],
  (report, dateTimeViewData) => {
    if (report && dateTimeViewData) {
      const { rangeStart, rangeEnd } = report;
      if (
        isDateAfter(
          moment(rangeStart).add(dateIntervals(dateTimeViewData).length - 1, 'days'),
          rangeEnd,
        )
      ) {
        return DATE_FORMAT_WITH_HOUR_MINS;
      }
    }
    return DATE_FORMAT;
  },
);

const selectHighLevelMetricsHash = createSelector([selectHighLevelMetrics], (highLevelMetrics) =>
  highLevelMetrics ? highLevelMetrics.reduce((acc, m) => ({ ...acc, [m.enumName]: m }), {}) : {},
);

const selectedSortedEntityViewDataById = createSelector([selectEntityViewData], (entityViewData) =>
  entityViewData.length
    ? lorderBy(entityViewData, [(entity) => entity.campaign.id], ['desc'])
    : entityViewData,
);

const nameSort = (entityViewDataSortedById, sorting) =>
  entityViewDataSortedById.sort((a, b) => {
    if (a.campaign.name === b.campaign.name) {
      return 0;
    }
    if (sorting === 'ascending') {
      if (a.campaign.name < b.campaign.name) {
        return 1;
      }
      return -1;
    }
    if (a.campaign.name < b.campaign.name) {
      return -1;
    }
    return 1;
  });

const idSort = (entityViewDataSortedById, sorting) =>
  entityViewDataSortedById.sort((a, b) =>
    sorting === 'ascending' ? a.campaign.id - b.campaign.id : b.campaign.id - a.campaign.id,
  );

const highLevelMetricSort = (entityViewData, sortedMetric, sorting) =>
  lorderBy(
    entityViewData,
    [(entity) => entity.highLevelMetrics[sortedMetric.enumName] || 0],
    [sorting === 'ascending' ? 'asc' : 'desc'],
  );

const selectSortedEntityViewData = createSelector(
  [
    selectEntityViewData,
    selectEntityViewTableConfigOrderBy,
    selectEntityViewTableConfigSorting,
    selectHighLevelMetrics,
    selectedSortedEntityViewDataById,
  ],
  (entityViewData, orderBy, sorting, highLevelMetrics, entityViewDataSortedById) => {
    if (sorting && orderBy) {
      if (orderBy === 'Name') {
        return nameSort(entityViewDataSortedById, sorting);
      }
      if (orderBy === 'ID') {
        return idSort(entityViewDataSortedById, sorting);
      }
      const sortedMetric = highLevelMetrics.find((metric) => metric.name === orderBy);
      if (sortedMetric) {
        return highLevelMetricSort(entityViewData, sortedMetric, sorting);
      }
    }
    return entityViewDataSortedById;
  },
);

const selectDateTimeViewDataSortedByTimeStamp = createSelector(
  [selectDateTimeViewData],
  (dateTimeViewData) =>
    [
      ...new Set(
        flatten(
          dateTimeViewData ? dateTimeViewData.map((metric) => metric.data.map((d) => d.time)) : [],
        ),
      ),
    ].sort(),
);

/* Returns an Array of numbers representing timestamps sorted by the selected metric in the table config.
 * If there's no sorting metric in the table, the timstamps are sorted chronologically.
 */
const selectSortedDateTimeViewIntervals = createSelector(
  [
    selectDateTimeViewData,
    selectEntityViewTableConfigOrderBy,
    selectEntityViewTableConfigSorting,
    selectDateTimeViewDataSortedByTimeStamp,
  ],
  (dateTimeViewData, orderBy, sorting, sortedDateTimeViewData) => {
    if (sorting && orderBy) {
      if (orderBy === 'Date') {
        return sortedDateTimeViewData.sort((a, b) => (sorting === 'ascending' ? a - b : b - a));
      }
      // First we need to find the data Object holding the metric the table is being sorted on.
      const sortedMetric = dateTimeViewData.find((metric) => metric.metric.name === orderBy);
      if (sortedMetric && sortedMetric.data.length > 0) {
        // The data is held in an array of objects that map timestamp to metric value. We need to
        // arrange the elements in the array to be in order based on the metric value.
        const sortedDataMetrics = lorderBy(
          sortedMetric.data,
          [(data) => data.value || 0],
          [sorting === 'ascending' ? 'asc' : 'desc'],
        );

        // Not all metric data objects contain every timestamp displayed on the table, therefore we
        // need to collect a set of all timestamps.
        const sortedTimestamps = sortedDataMetrics.map((d) => d.time);
        const returnedMetric = [
          ...new Set(
            sortedTimestamps.concat(
              flatten(dateTimeViewData.map((metric) => metric.data.map((d) => d.time))),
            ),
          ),
        ];
        return returnedMetric;
      }
    }
    // If the tableConfig does not have a sorting or orderby metric, return a deduped array of
    // timestamps and sort them.
    return sortedDateTimeViewData;
  },
);

export const getReportDataSourceTypeIdsInModal = (state) =>
  dataSourceModal(state).selectedTypeIdsInModal;

export const selectReportDataSourceModal = createSelector(
  [selectReportDetails, getActiveReport],
  (reportDetails, report) => ({
    ...reportDetails.tableState,
    reportingEntities: reportDetails.dataSourceModal.reportingEntities,
    total: reportDetails.dataSourceModal.reportingEntitiesTotal,
    selectedTypeIds: reportDetails.dataSourceModal.selectedTypeIds,
    selectedTypeIdsInModal: reportDetails.dataSourceModal.selectedTypeIdsInModal,
    tableConfig: reportDetails.dataSourceModal.tableConfig,
    isLoading: reportDetails.dataSourceModal.isLoading,
    componentType: report.componentType || 'Campaign',
  }),
);

/*
 * Returns an object containing data for the entity data view chart. The returned object has
 * 2 properties: categories and data. Categories contains the name of the campaigns, and data
 * is a list of objects, where each object consists of a metric, and a list of values pertaining
 * to that metric for each campaign.
 */
const selectReportEntityChartData = createSelector(
  [selectedSortedEntityViewDataById],
  (entityViewData) => {
    if (entityViewData.length) {
      const chartDataMap = {};
      Object.keys(entityViewData[0].highLevelMetrics).forEach((metric) => {
        chartDataMap[metric] = {
          id: metric,
          name: metric,
          data: [],
        };
      });
      const categories = [];
      entityViewData.forEach((entityData) => {
        categories.push(entityData.campaign.name);
        Object.keys(entityData.highLevelMetrics).forEach((metric) =>
          chartDataMap[metric].data.push(entityData.highLevelMetrics[metric]),
        );
      });
      return {
        data: Object.values(chartDataMap),
        categories,
      };
    }

    return {};
  },
);

/*
 * Returns an object containing data for the datetime view chart. The returned object has
 * 2 properties: categories and data. Categories contains the formatted name of each timestamp,
 * and data is a list of objects, where each object consists of a metric, and a list of values
 * pertaining to that metric for each dateTime.
 */
const selectReportDateTimeChartData = createSelector(
  [selectDateTimeViewData, selectReportDateFormat],
  (dateTimeViewData, dateFormat) => {
    if (dateTimeViewData.length) {
      const intervals = dateIntervals(dateTimeViewData);
      if (intervals.length > 0) {
        const categories = intervals.map((timestamp) => moment(timestamp).format(dateFormat));
        const data = dateTimeViewData.map((m) => ({
          name: m.metric.name,
          id: m.metric.name,
          data: m.data.map((metricsData) => metricsData.value),
        }));
        return {
          categories,
          data,
        };
      }
    }
    return {};
  },
);

export const selectActiveChartViewData = createSelector(
  [
    selectReportDetailsChartActiveView,
    selectReportDateTimeChartData,
    selectReportEntityChartData,
    selectHighLevelMetricsHash,
  ],
  (activeView, dateTimeChartData, entityChartData, highLevelMetricsHash) => {
    const activeChartViewData =
      activeView === ActiveMenuItemEnum.componentTable ? entityChartData : dateTimeChartData;
    return {
      ...activeChartViewData,
      data: activeChartViewData.data
        ? activeChartViewData.data.map((series) => {
            const metric = highLevelMetricsHash[series.id];
            const isPercentage = isMetricPercent(metric);
            return {
              ...series,
              name: metric ? metric.name : series.name,
              data: series.data.map((r) => {
                if (r === null) return null;
                return isPercentage ? Math.round(r * 10000) / 100 : r;
              }),
              yAxis: isPercentage ? 1 : 0,
              marker: {
                symbol: isPercentage ? 'triangle' : 'square',
              },
              tooltip: {
                valueSuffix: isPercentage ? '%' : '',
              },
            };
          })
        : [],
    };
  },
);

export const selectReportChartState = createSelector(
  [selectReportDetailsChartState, selectActiveChartViewData],
  (chartState, activeChartViewData) => ({
    ...chartState,
    config: {
      ...chartState.config,
      title: {
        text:
          activeChartViewData.data && activeChartViewData.data.length > 0
            ? ''
            : 'Select metrics to see report data',
      },
      xAxis: {
        ...chartState.config.xAxis,
        categories: activeChartViewData.categories,
      },
      data: activeChartViewData.data,
    },
  }),
);

export const selectReportTableState = createSelector(
  [
    selectReportDetailsTableState,
    getEntityViewTableConfig,
    selectSortedDateTimeViewIntervals,
    selectReportDateFormat,
  ],
  (tableState, tableConfig, sortedDateIntervals, dateFormat) => {
    const data = [];
    const columns = createDateTimeViewColumns(tableState?.dateTimeViewData || []);
    if (tableState.dateTimeViewData && sortedDateIntervals) {
      tableState.dateTimeViewData.forEach((m) => {
        const { enumName } = m.metric;
        m.data.forEach((dataObj, i) => {
          const datum = data[i] || {};
          data[i] = {
            ...datum,
            id: dataObj.time,
            [enumName]: formatNumber(enumName, dataObj.value),
            timestamp: dataObj.time,
            dateFormat,
          };
        });
      });
    }

    return {
      ...tableState,
      columns,
      data,
      total: sortedDateIntervals.length,
      tableConfig,
    };
  },
);

export const selectReportingObject = createSelector([selectReportById], (report) => report || {});

export const selectReportingEntityTableTotal = createSelector(
  [selectReportDetails],
  (reportDetails) => reportDetails.tableState.reportingEntitiesTotal,
);

export const selectFormattedHighLevelMetrics = createSelector(
  [selectHighLevelMetrics],
  (highLevelMetrics) =>
    highLevelMetrics.map((m) => ({
      value: m.enumName,
      text: m.name,
      key: m.enumName,
    })),
);

export const selectReportMetrics = createSelector(
  [getActiveReport],
  (report) => report && report.selectedMetrics,
);

export const selectSelectedMetrics = createSelector(
  [selectReportMetrics, selectFormattedHighLevelMetrics],
  (selectedMetrics, highLevelMetrics) =>
    selectedMetrics &&
    highLevelMetrics.map((m) => ({
      ...m,
      selected: !!selectedMetrics.find((sm) => sm.enumName === m.value),
    })),
);

export const selectSelectedHighLevelMetrics = createSelector(
  [selectSelectedMetrics],
  (selectedMetrics) =>
    (selectedMetrics && selectedMetrics.filter((m) => m.selected).map((m) => m.value)) || [],
);

export const selectSummaryViewData = createSelector(
  [selectSortedEntityViewData, selectHighLevelMetrics, getEntityViewTableConfig],
  (data, highLevelMetrics, tableConfig) => {
    if (data.length < 1) return {};
    const highLevelMetricHeaders = Object.keys(data[0].highLevelMetrics);
    const columns = createSummaryViewColumns(highLevelMetricHeaders, highLevelMetrics);

    const mappedData = data.map((summaryData) => {
      return {
        ...summaryData.campaign,
        ...summaryData.highLevelMetrics,
        link: getCampaignRoute(summaryData.campaign?.id, summaryData.campaign?.campaignState),
      };
    });

    return {
      columns,
      data: mappedData,
      total: mappedData?.length || 0,
      tableConfig,
    };
  },
);

export const selectLoadingHighLevelMetrics = (state) => state.metrics.loadingHighLevelMetrics;

const selectEntities = createSelector([selectReportDetails], (reportDetails) =>
  // TODO ugh this doesn't apply to workflows. of course it doesn't
  // return an object keyed by entity id
  keyBy(
    reportDetails.tableState.summaryViewData.data.map((d) => d.campaign),
    'id',
  ),
);
export const selectEntityPills = createSelector(
  [selectReportDetails, selectEntities],
  (reportDetails, entities) =>
    reportDetails.dataSourceModal.selectedTypeIdsInModal
      ? reportDetails.dataSourceModal.selectedTypeIdsInModal.map((id) => {
          const entity = entities[id];
          return entity
            ? {
                text: `${id}: ${entity.name}`,
                value: id,
              }
            : { text: `${id}`, value: id };
        })
      : [],
);
